import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles, ShoppingBag, Calculator, Settings, Bot, User, Minimize2, Maximize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { assistantName, brandName } from '../hooks/useCompanyBrand';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ASSISTANT_TYPES = {
  customer_support: {
    id: 'customer_support',
    get name() { return `${assistantName()} Support`; },
    description: 'Get help with products, orders, and more',
    icon: MessageCircle,
    color: 'bg-blue-500',
    gradient: 'from-blue-500 to-blue-600'
  },
  product_recommendations: {
    id: 'product_recommendations',
    name: 'Product Advisor',
    description: 'Find the perfect window treatments',
    icon: ShoppingBag,
    color: 'bg-emerald-500',
    gradient: 'from-emerald-500 to-emerald-600'
  },
  quote_assistant: {
    id: 'quote_assistant',
    name: 'Quote Assistant',
    description: 'Get pricing estimates',
    icon: Calculator,
    color: 'bg-amber-500',
    gradient: 'from-amber-500 to-amber-600'
  },
  admin_assistant: {
    id: 'admin_assistant',
    name: 'Admin Helper',
    description: 'CMS and operations support',
    icon: Settings,
    color: 'bg-purple-500',
    gradient: 'from-purple-500 to-purple-600'
  }
};

export const AIChatWidget = ({ 
  defaultAssistant = 'customer_support',
  isAdmin = false,
  position = 'bottom-right' // bottom-right, bottom-left
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [assistantType, setAssistantType] = useState(defaultAssistant);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showAssistantPicker, setShowAssistantPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const currentAssistant = ASSISTANT_TYPES[assistantType];
  const AssistantIcon = currentAssistant.icon;

  // Available assistants based on user type
  const availableAssistants = isAdmin 
    ? Object.values(ASSISTANT_TYPES)
    : Object.values(ASSISTANT_TYPES).filter(a => a.id !== 'admin_assistant');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Add welcome message when assistant changes
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessages = {
        customer_support: `Hi! I'm ${assistantName()}, your AI assistant. How can I help you today with our window treatments?`,
        product_recommendations: "Hello! I'm here to help you find the perfect window treatments. Tell me about your room and what you're looking for!",
        quote_assistant: "Hi there! I can help you estimate costs for your window treatment project. How many windows are you looking to cover?",
        admin_assistant: "Hello! I'm your Admin Assistant. I can help you navigate the CMS, manage orders, and answer questions about the system."
      };
      
      setMessages([{
        role: 'assistant',
        content: welcomeMessages[assistantType]
      }]);
    }
  }, [isOpen, assistantType]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      // Use streaming endpoint
      const response = await fetch(`${API_URL}/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          assistant_type: assistantType
        })
      });

      if (!response.ok) {
        throw new Error('Chat request failed');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';

      // Add empty assistant message that we'll update
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            
            if (data === '[DONE]') {
              continue;
            }
            
            if (data.startsWith('[SESSION_ID:')) {
              const newSessionId = data.slice(12, -1);
              setSessionId(newSessionId);
              continue;
            }
            
            assistantMessage += data;
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = {
                role: 'assistant',
                content: assistantMessage
              };
              return newMessages;
            });
          }
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const switchAssistant = (newType) => {
    setAssistantType(newType);
    setMessages([]);
    setSessionId(null);
    setShowAssistantPicker(false);
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  const positionClasses = position === 'bottom-left' 
    ? 'left-4 sm:left-6' 
    : 'right-4 sm:right-6';

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setIsOpen(true)}
            className={`floating-action fixed bottom-4 sm:bottom-6 ${positionClasses} z-50 p-4 bg-gradient-to-r ${currentAssistant.gradient} text-white rounded-full shadow-lg hover:shadow-xl transition-shadow`}
            data-testid="ai-chat-button"
          >
            <Sparkles className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`fixed ${positionClasses} bottom-4 sm:bottom-6 z-50 ${
              isExpanded 
                ? 'w-[calc(100%-2rem)] sm:w-[600px] h-[calc(100vh-2rem)] sm:h-[700px]' 
                : 'w-[calc(100%-2rem)] sm:w-[400px] h-[500px] sm:h-[550px]'
            } bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-neutral-200`}
            data-testid="ai-chat-window"
          >
            {/* Header */}
            <div className={`bg-gradient-to-r ${currentAssistant.gradient} text-white p-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <AssistantIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{currentAssistant.name}</h3>
                    <p className="text-xs text-white/80">{currentAssistant.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAssistantPicker(!showAssistantPicker)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    title="Switch Assistant"
                  >
                    <Bot className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors hidden sm:block"
                    title={isExpanded ? 'Minimize' : 'Expand'}
                  >
                    {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    title="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Assistant Picker */}
              <AnimatePresence>
                {showAssistantPicker && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 pt-3 border-t border-white/20"
                  >
                    <p className="text-xs text-white/70 mb-2">Switch Assistant:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {availableAssistants.map((assistant) => {
                        const Icon = assistant.icon;
                        return (
                          <button
                            key={assistant.id}
                            onClick={() => switchAssistant(assistant.id)}
                            className={`flex items-center gap-2 p-2 rounded-lg text-xs transition-colors ${
                              assistantType === assistant.id 
                                ? 'bg-white/30' 
                                : 'bg-white/10 hover:bg-white/20'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{assistant.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-neutral-50">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-start gap-2 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user' 
                        ? 'bg-neutral-700' 
                        : `bg-gradient-to-r ${currentAssistant.gradient}`
                    }`}>
                      {message.role === 'user' 
                        ? <User className="w-4 h-4 text-white" />
                        : <AssistantIcon className="w-4 h-4 text-white" />
                      }
                    </div>
                    <div className={`p-3 rounded-2xl ${
                      message.role === 'user'
                        ? 'bg-neutral-700 text-white rounded-tr-sm'
                        : 'bg-white shadow-sm rounded-tl-sm'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
              
              {isLoading && messages[messages.length - 1]?.role === 'user' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-r ${currentAssistant.gradient}`}>
                      <AssistantIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white p-3 rounded-2xl rounded-tl-sm shadow-sm">
                      <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-3 bg-neutral-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-neutral-200"
                  disabled={isLoading}
                  data-testid="ai-chat-input"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className={`p-3 rounded-xl transition-colors ${
                    inputValue.trim() && !isLoading
                      ? `bg-gradient-to-r ${currentAssistant.gradient} text-white hover:opacity-90`
                      : 'bg-neutral-200 text-neutral-400'
                  }`}
                  data-testid="ai-chat-send"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-neutral-400 mt-2 text-center">
                Powered by GPT-4o • {brandName()} AI
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatWidget;
